import { z } from "zod";

export const MAX_PAGE_SIZE = 100;
export const DEFAULT_PAGE_SIZE = 25;

export const pageQuerySchema = z.object({
  cursor: z.string().trim().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(MAX_PAGE_SIZE).default(DEFAULT_PAGE_SIZE),
});

export interface Page<T> {
  items: T[];
  /** Opaque cursor for the next page, or null on the last page. */
  nextCursor: string | null;
}

/** A page for a table: also how many records match the filters, for "26–50 of 132". */
export interface TablePage<T> extends Page<T> {
  total: number;
}

/**
 * A sort parameter for a table: one of `keys`, or "-" plus one for descending ("-lastLoginAt").
 * Anything else is a validation error, so only indexed, allow-listed columns are ever sorted on.
 */
export function sortSchema<const K extends string>(
  keys: readonly [K, ...K[]],
  fallback: K | `-${K}`,
) {
  const values = [...keys, ...keys.map((key) => `-${key}` as const)] as [
    K | `-${K}`,
    ...(K | `-${K}`)[],
  ];
  return z
    .enum(values, { error: "Choose a column to sort by" })
    .optional()
    .transform((value): K | `-${K}` => value ?? fallback);
}

/** "-name" → { key: "name", dir: "desc" }. */
export function readSort<K extends string>(sort: K | `-${K}`): { key: K; dir: "asc" | "desc" } {
  return sort.startsWith("-")
    ? { key: sort.slice(1) as K, dir: "desc" }
    : { key: sort as K, dir: "asc" };
}
